import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, FileText, Gauge, Hash, Timer } from 'lucide-react';
import { api } from '@/lib/api';
import { LogView } from '@/components/jobs/LogView';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/Badge';
import { ErrorState, PageLoader } from '@/components/ui/States';
import { useToast } from '@/components/ui/Toast';
import { formatDateTime, formatDuration, formatNumber, truncate } from '@/lib/format';

/**
 * Detail satu eksekusi crawl, lengkap dengan progres langsung dan log.
 *
 * Layar ini juga tidak ada di rancangan. Padahal ketika sebuah crawl gagal,
 * inilah satu-satunya tempat pengguna bisa mengetahui penyebabnya - tanpa itu
 * mereka hanya melihat lencana merah "Gagal" tanpa jalan keluar.
 */
export const RunDetailPage = () => {
  const { id, runId } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['run', runId],
    queryFn: () => api.get(`/jobs/${id}/runs/${runId}`),
    // Selama run berjalan, polling menjadi cadangan bila WebSocket terputus.
    refetchInterval: (query) =>
      ['queued', 'running'].includes(query.state.data?.data?.status) ? 2_000 : false,
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/jobs/${id}/runs/${runId}/cancel`),
    onSuccess: () => {
      toast.info('Pembatalan diminta', {
        description: 'Crawl akan berhenti setelah halaman yang sedang diproses selesai.',
      });
      queryClient.invalidateQueries({ queryKey: ['run', runId] });
    },
    onError: (err) => toast.error('Gagal membatalkan', { description: err.message }),
  });

  if (isLoading) return <PageLoader label="Memuat detail run…" />;
  if (isError) {
    return (
      <Card>
        <ErrorState message={error?.message} requestId={error?.requestId} onRetry={refetch} />
      </Card>
    );
  }

  const run = data.data;
  const isActive = ['queued', 'running'].includes(run.status);
  const progress = run.progress ?? {};

  return (
    <div className="space-y-4">
      <Link
        to={`/jobs/${id}`}
        className="inline-flex items-center gap-1.5 text-sm text-[var(--text-muted)] transition-colors hover:text-[var(--text-secondary)]"
      >
        <ArrowLeft className="size-3.5" />
        Kembali ke job
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="text-xl font-extrabold tracking-tight text-[var(--text-primary)]">
              Run {formatDateTime(run.createdAt)}
            </h2>
            <StatusBadge status={run.status} />
          </div>
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            {run.jobId?.name ?? 'Job'} · dipicu{' '}
            {run.trigger === 'schedule' ? 'oleh jadwal' : 'secara manual'}
            {run.attempt > 1 && ` · percobaan ke-${run.attempt}`}
          </p>
        </div>

        {isActive && (
          <Button
            variant="dangerOutline"
            icon={Ban}
            onClick={() => cancelMutation.mutate()}
            loading={cancelMutation.isPending}
          >
            Batalkan Run
          </Button>
        )}
      </div>

      {/* Bar progres langsung */}
      {isActive && (
        <Card className="overflow-hidden">
          <CardBody>
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-[var(--text-primary)]">
                Sedang memproses…
              </span>
              <span className="font-bold tabular text-accent-600 dark:text-accent-400">
                {progress.percent ?? 0}%
              </span>
            </div>

            <div
              className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--surface-sunken)]"
              role="progressbar"
              aria-valuenow={progress.percent ?? 0}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Progres crawl"
            >
              <div
                className="h-full rounded-full bg-accent-600 transition-[width] duration-500 ease-out"
                style={{ width: `${progress.percent ?? 0}%` }}
              />
            </div>

            {progress.currentUrl && (
              <p className="mt-2 truncate font-mono text-xs text-[var(--text-muted)]">
                {truncate(progress.currentUrl, 90)}
              </p>
            )}
          </CardBody>
        </Card>
      )}

      {/* Angka-angka run */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <RunStat
          icon={FileText}
          label="Halaman dikunjungi"
          value={`${formatNumber(progress.pagesVisited ?? 0)}${
            progress.pagesTotal ? ` / ${formatNumber(progress.pagesTotal)}` : ''
          }`}
        />
        <RunStat
          icon={Hash}
          label="Baris terkumpul"
          value={formatNumber(progress.recordsExtracted ?? 0)}
        />
        <RunStat icon={Timer} label="Durasi" value={formatDuration(run.durationMs)} />
        <RunStat
          icon={Gauge}
          label="Mesin"
          value={{ static: 'HTML statis', dynamic: 'Browser' }[run.engineUsed] ?? ' - '}
        />
      </div>

      {run.error?.message && (
        <Card className="border-danger-200 dark:border-danger-500/25">
          <CardHeader title="Penyebab kegagalan" />
          <CardBody>
            <p className="text-sm text-danger-700 dark:text-danger-500">{run.error.message}</p>
            {run.error.code && (
              <code className="mt-2 inline-block rounded bg-[var(--surface-sunken)] px-2 py-1 font-mono text-xs text-[var(--text-muted)]">
                {run.error.code}
              </code>
            )}
          </CardBody>
        </Card>
      )}

      {/* Log eksekusi */}
      <Card className="overflow-hidden">
        <CardHeader
          title="Log Eksekusi"
          description="Catatan langkah demi langkah selama crawl berlangsung."
        />

        <div className="px-5 pb-5">
          <LogView
            logs={run.logs}
            berjalan={run.status === 'queued' || run.status === 'running'}
          />
        </div>
      </Card>

      {/* Statistik teknis, untuk yang ingin menelusuri lebih jauh */}
      <Card>
        <CardHeader title="Statistik Teknis" />
        <CardBody>
          <dl className="grid gap-x-8 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              ['Permintaan HTTP', formatNumber(run.stats?.httpRequests ?? 0)],
              ['Permintaan gagal', formatNumber(run.stats?.httpErrors ?? 0)],
              ['Duplikat dibuang', formatNumber(run.stats?.duplicatesRemoved ?? 0)],
              ['Baris dilewati', formatNumber(run.stats?.recordsSkipped ?? 0)],
              ['Dimulai', formatDateTime(run.startedAt)],
              ['Selesai', formatDateTime(run.finishedAt)],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-4 text-sm">
                <dt className="text-[var(--text-muted)]">{label}</dt>
                <dd className="font-medium tabular text-[var(--text-primary)]">{value}</dd>
              </div>
            ))}
          </dl>
        </CardBody>
      </Card>
    </div>
  );
};

const RunStat = ({ icon: Icon, label, value }) => (
  <Card className="flex items-center gap-3 p-4">
    <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-[var(--surface-sunken)] text-[var(--text-muted)]">
      <Icon className="size-[18px]" aria-hidden="true" />
    </div>
    <div className="min-w-0">
      <p className="truncate text-lg font-extrabold tabular text-[var(--text-primary)]">{value}</p>
      <p className="truncate text-xs text-[var(--text-muted)]">{label}</p>
    </div>
  </Card>
);
